import { encodeClientWsMessage } from '@orborix/shared-types';
import { AudioPlayer } from '../AudioPlayer.js';
import { isMicDisposedError, MicCapture } from '../MicCapture.js';
import { DEFAULT_PATH } from './constants.js';
import { countSettleDrop, type SettleDropCounter } from './micSettleCounter.js';
import { wsBaseUrl } from './utils.js';
import type {
  AudioPlayerLike,
  ClientIOMode,
  MicCaptureLike,
  UseVoiceSessionOptions,
  VoiceState,
} from './types.js';

export interface BuildSessionUrlInput {
  opts: UseVoiceSessionOptions;
  /**
   * Auto-reconexión: si está, se anexa `?reconnectSessionId=` al handshake —
   * la señal de "re-pegate a ESTA fila" (mismo sessionId, misma numeración de
   * turnos, un solo bill). El backend reusa la fila si es reattach-elegible.
   *
   * Es una señal SEPARADA de `resumeSessionId` (que puede venir en
   * `opts.moduleHints` para "Continuar conversación" = fila hija nueva con
   * contexto heredado): NO lo pisa. Así la reconexión no se confunde con
   * retomar una charla vieja del historial.
   */
  reconnectSessionId?: string;
}

/** Arma la URL del WS desde las opciones del hook (incluye query params). */
export function buildSessionUrl({ opts, reconnectSessionId }: BuildSessionUrlInput): string {
  const baseUrl = opts.url ?? `${wsBaseUrl()}${DEFAULT_PATH}`;
  const queryParams = new URLSearchParams();
  if (opts.moduleSlug) queryParams.set('moduleSlug', opts.moduleSlug);
  if (opts.captureMode) queryParams.set('captureMode', opts.captureMode);
  // Speech rate inicial (valor real del provider, no label). Si el user
  // tiene 1.2 persistido para `english`, el primer turno ya arranca a esa
  // velocidad sin esperar a un `speech-rate.change` post-arranque.
  if (typeof opts.initialSpeechRate === 'number') {
    queryParams.set('speechRate', String(opts.initialSpeechRate));
  }
  // IANA tz del navegador (ej. "America/Argentina/Buenos_Aires"). Backend
  // valida con resolveUserTimezone(); inválido cae a UTC con warn.
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) queryParams.set('clientTz', tz);
  } catch {
    /* old browser — server fallbacks to UTC */
  }
  if (opts.moduleHints) {
    for (const [k, v] of Object.entries(opts.moduleHints)) {
      if (v != null) queryParams.set(k, v);
    }
  }
  // Auto-reconexión: señal separada (NO pisa `resumeSessionId` de moduleHints,
  // que es "Continuar conversación"). Ver doc del campo.
  if (reconnectSessionId) queryParams.set('reconnectSessionId', reconnectSessionId);
  const qs = queryParams.toString();
  return qs ? `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}${qs}` : baseUrl;
}

/** Crea AudioPlayer + MicCapture respetando los factories de los tests. */
export async function createPlayerAndMic(opts: UseVoiceSessionOptions): Promise<{
  player: AudioPlayerLike;
  mic: MicCaptureLike;
}> {
  const player = opts.playerFactory ? opts.playerFactory() : new AudioPlayer();
  await player.unlock();
  const mic = opts.micFactory ? opts.micFactory() : new MicCapture();
  return { player, mic };
}

export interface OnOpenInput {
  ws: WebSocket;
  mic: MicCaptureLike;
  startMode: ClientIOMode;
  setState: (next: VoiceState) => void;
  /**
   * ¿Vale reintentar el `start()` que un `dispose()` canceló en vuelo? Lo
   * responde el hook, que es el único que sabe si ESE dispose fue deliberado:
   * false para el cierre de la sesión, el teardown de background, el de
   * pantalla bloqueada y el mode-switch a texto (ahí tener el mic apagado es
   * lo que se quiso, revivirlo sería la regresión), true para un dispose
   * accidental con la sesión viva, en voz y con el documento visible. No tiene default a propósito: un `?? true` reintentaría
   * teardowns deliberados y un `?? false` desactivaría la defensa entera.
   */
  canRetryMicStart: () => boolean;
}

/**
 * Arranca el mic tolerando UN `mic_disposed`: si un `dispose()` canceló el
 * bootstrap en vuelo (y el hook confirma que no fue deliberado), se reintenta
 * una única vez. Sin esto la sesión queda viva pero MUDA —ondas quietas, cero
 * PCM— hasta que un evento externo (foreground, mode-switch) la rescate.
 *
 * Un reintento y no un loop: si el segundo `start()` también lo cancelan, hay
 * un dispose sostenido (o el mic está roto) y reintentar en círculos taparía
 * el problema en vez de mostrarlo.
 */
async function startMicWithRetry(mic: MicCaptureLike, canRetry: () => boolean): Promise<void> {
  try {
    await mic.start();
    return;
  } catch (err: unknown) {
    // Permiso denegado / device roto: no es cancelación, no se reintenta.
    if (!isMicDisposedError(err)) {
      console.warn('[voice] mic start failed', err);
      return;
    }
    if (!canRetry()) {
      console.warn('[voice] mic start cancelled by a deliberate dispose — no retry');
      return;
    }
  }
  try {
    await mic.start();
    console.warn('[voice] mic start retried after mic_disposed');
  } catch (err: unknown) {
    console.warn('[voice] mic start retry failed', err);
  }
}

/**
 * Handler del `open` del WS. Manda `start`, propaga el modo I/O persistido
 * y arranca el mic — excepto en modo `input=text` (videos arranca chat-only,
 * sin mic permission popup).
 */
export function onWsOpen({ ws, mic, startMode, setState, canRetryMicStart }: OnOpenInput): void {
  try {
    ws.send(encodeClientWsMessage({ type: 'start' }));
  } catch {
    /* ignore */
  }
  if (startMode.input !== 'voice' || startMode.output !== 'voice') {
    try {
      ws.send(encodeClientWsMessage({ type: 'mode.switch', mode: startMode }));
    } catch {
      /* ignore */
    }
  }
  if (startMode.input !== 'text') {
    // El `start()` puede rechazar por permiso denegado o porque un
    // `dispose()` canceló el bootstrap en vuelo (`mic_disposed`, ej. cerrar
    // la sesión apenas abre el WS). Se loguea siempre y —sólo si la
    // cancelación no fue deliberada— se reintenta una vez. Nunca tumba el
    // handler del `open` ni deja una unhandled rejection.
    void startMicWithRetry(mic, canRetryMicStart);
  }
  setState('listening');
}

export interface MicPcmHandlerDeps {
  mutedRef: { current: boolean };
  pausedRef: { current: boolean };
  captureExternallyPausedRef: { current: boolean };
  stateRef: { current: VoiceState };
  micSettleUntilRef: { current: number };
  playerRef: { current: AudioPlayerLike | null };
  wsRef: { current: WebSocket | null };
  /**
   * Modo realtime (S2S full-duplex, plan realtime §6): `true` cuando el
   * handshake `session.started` anunció `conversationMode: 'realtime'`. En
   * full-duplex el audio se manda SIEMPRE (el barge-in lo detecta el
   * server-VAD de OpenAI) — los gates half-duplex/acoustic/settle se
   * bypasean; mute/pause siguen aplicando (son intención del user, no eco).
   */
  realtimeModeRef: { current: boolean };
  /**
   * Diagnóstico (no cambia NINGUNA decisión del audio): cuenta los bloques que
   * el gate de settle descarta trayendo audio no-cero. Ver `micSettleCounter`.
   */
  settleCounterRef: { current: SettleDropCounter };
}

/**
 * Handler de `mic.on('pcm')` con todos los gates (mute, pause, half-duplex,
 * acoustic gate, settle delay).
 */
export function createMicPcmHandler(deps: MicPcmHandlerDeps): (chunk: ArrayBuffer) => void {
  return (chunk: ArrayBuffer) => {
    if (deps.mutedRef.current) return;
    if (deps.pausedRef.current) return;
    if (deps.captureExternallyPausedRef.current) return;
    // Full-duplex (modo realtime): el audio viaja siempre — se bypasean los
    // gates half-duplex de abajo (el eco lo mitiga el AEC del browser + el
    // server-VAD de OpenAI; su tasa real de falso-barge-in es el gate
    // empírico de la Fase 0 del plan realtime).
    if (!deps.realtimeModeRef.current) {
      // Half-duplex: durante `speaking` y `thinking` no enviamos audio para
      // evitar que el TTS reproducido se cuele por el mic.
      const s = deps.stateRef.current;
      if (s === 'speaking' || s === 'thinking') return;
      // Acoustic gate: el server flipea `listening` cuando termina de emitir
      // TTS, pero los chunks PCM siguen sonando ~cientos de ms más.
      if (deps.playerRef.current?.isPlaying?.()) return;
      // Settle delay: primeros MIC_SETTLE_MS post-fin-acústico → reverb + decay.
      if (Date.now() < deps.micSettleUntilRef.current) {
        // El descarte se cuenta SÓLO si el bloque trae muestras no-cero: el
        // noise gate del worklet ya zerea el silencio, así que no-cero acá
        // significa que se tiró habla (o eco fuerte). Diagnóstico puro — la
        // decisión de descartar ya se tomó en la línea de arriba.
        countSettleDrop(deps.settleCounterRef.current, chunk);
        return;
      }
    }
    const sock = deps.wsRef.current;
    if (!sock || sock.readyState !== WebSocket.OPEN) return;
    try {
      sock.send(chunk);
    } catch {
      /* ignore */
    }
  };
}

/**
 * Teardown ordenado: detiene mic, libera MediaStream/AudioContext del mic
 * compartido, destruye player, manda `{type:'end'}` y cierra el WS.
 *
 * `silent` (default false): desconexión SIN fin deliberado — NO manda
 * `{type:'end'}`, sólo cierra el socket. El backend distingue el cierre por ese
 * mensaje: con `{type:'end'}` finaliza la fila como `client-end` (no
 * reattach-elegible, distill inmediato); sin él, como `client-disconnect`
 * (reattach-elegible por su ventana de gracia, distill diferido). El mic y el
 * player se liberan IGUAL en ambos modos (no queda audio sonando ni mic tomado).
 */
export async function teardownConnection(
  refs: {
    ws: WebSocket | null;
    mic: MicCaptureLike | null;
    player: AudioPlayerLike | null;
  },
  opts: { silent?: boolean } = {},
): Promise<void> {
  const { ws, mic, player } = refs;
  if (mic) {
    try {
      await mic.stop();
    } catch {
      /* ignore */
    }
  }
  // Liberar el MediaStream + AudioContext del mic compartido — sin esto el
  // indicador rojo del browser y el "boop" de iOS siguen activos. Primero el
  // scope de la captura (si es una de `createMicCapture()`, el estático no la
  // alcanza) y después el de proceso, que es el default y el que usa el hook.
  // Ambos son idempotentes: para una captura default, el segundo es no-op.
  try {
    await mic?.disposeShared?.();
  } catch {
    /* ignore */
  }
  try {
    await MicCapture.dispose();
  } catch {
    /* ignore */
  }
  if (player) {
    try {
      await player.destroy();
    } catch {
      /* ignore */
    }
  }
  try {
    if (ws && ws.readyState <= WebSocket.OPEN) {
      if (!opts.silent) ws.send(encodeClientWsMessage({ type: 'end' }));
      ws.close();
    }
  } catch {
    /* ignore */
  }
}
